import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateLong, formatDateTime, formatTime } from '@mdt/domain';
import { useSession } from './session.tsx';

/** Dates in Asia/Bangkok, in the person's language and calendar (BE/CE) — the same rules the server uses. */
export function useFormat() {
  const { i18n } = useTranslation();
  const { calendar } = useSession();
  const locale = i18n.language === 'th' ? 'th' : 'en';
  return {
    dateTime: (at: string | number | Date) => formatDateTime(at, locale, calendar),
    date: (at: string | number | Date) => formatDate(at, locale, calendar),
    dateLong: (at: string | number | Date) => formatDateLong(at, locale, calendar),
    time: (at: string | number | Date) => formatTime(at, locale),
    bytes: (n: number) => formatBytes(n, locale),
    /** "in 2 days", "3 hours ago": the largest unit that is at least 1. */
    relative: (at: string | number | Date, now: number = Date.now()) => formatRelative(new Date(at).getTime() - now, locale),
    duration: (seconds: number) => formatDuration(seconds, locale),
  };
}

const UNITS = ['B', 'kB', 'MB', 'GB'] as const;

/** Binary multiples with SI-looking names, as file managers show them: 1 MB = 1,048,576 bytes. */
export function formatBytes(n: number, locale: 'th' | 'en'): string {
  let v = n;
  let u = 0;
  while (v >= 1024 && u < UNITS.length - 1) {
    v /= 1024;
    u++;
  }
  const digits = u === 0 || v >= 100 ? 0 : 1;
  return `${new Intl.NumberFormat(locale === 'th' ? 'th-TH-u-nu-latn' : 'en-GB', { maximumFractionDigits: digits }).format(v)} ${UNITS[u]}`;
}

function formatRelative(deltaMs: number, locale: 'th' | 'en'): string {
  const rtf = new Intl.RelativeTimeFormat(locale === 'th' ? 'th-TH-u-nu-latn' : 'en-GB', { numeric: 'auto' });
  const s = Math.round(deltaMs / 1000);
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, 'second');
  if (abs < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (abs < 86400 * 2) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
}

function formatDuration(seconds: number, locale: 'th' | 'en'): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.max(0, Math.round(seconds % 60));
  const unit = (v: number, u: 'hour' | 'minute' | 'second') =>
    new Intl.NumberFormat(locale === 'th' ? 'th-TH-u-nu-latn' : 'en-GB', { style: 'unit', unit: u, unitDisplay: 'short' }).format(v);
  if (h > 0) return `${unit(h, 'hour')} ${unit(m, 'minute')}`;
  if (m > 0) return `${unit(m, 'minute')} ${unit(s, 'second')}`;
  return unit(s, 'second');
}

/** The Bangkok wall-clock date and time of an instant, as <input type="date"> and <input type="time" step="1"> values. */
export function bangkokParts(at: string | Date): { date: string; time: string } {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(at)).map((p) => [p.type, p.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}:${parts.second}` };
}

/** Bangkok has no daylight saving: a wall-clock date and time there is always UTC+07:00. */
export const bangkokInstant = (date: string, time: string): string => `${date}T${time.length === 5 ? `${time}:00` : time}+07:00`;

/** Sets the document title ("… · MDT"), which screen readers announce on navigation. */
export function usePageTitle(title: string) {
  useEffect(() => {
    document.title = `${title} · MDT`;
  }, [title]);
}

/** A person's name in the reading language: English when chosen and known, else the Thai name of record. */
export function usePersonName() {
  const { i18n } = useTranslation();
  const en = i18n.language !== 'th';
  return (x: { nameTh: string; nameEn?: string | null }) => (en && x.nameEn ? x.nameEn : x.nameTh);
}
